import React, { useState, useEffect } from 'react';
import { CgMenuGridR } from "react-icons/cg";
import { GrUserSettings } from "react-icons/gr";
import { FaMoneyBillTransfer } from "react-icons/fa6";
import { MdOutlineAnalytics, MdPendingActions } from "react-icons/md";
import { Link, useLocation } from 'react-router-dom';
import { peekVisitedUser, VisitedUserNavState } from '../../../helpers/adminVisitedUserCache.tsx';
import { IoCloseCircleOutline } from "react-icons/io5";
import { GiReceiveMoney } from "react-icons/gi";
import { GrDocumentPerformance } from "react-icons/gr";
import { logUserActivity } from '../../../services/player/LoginService.tsx';
import { useUser } from '../../../context/UserContext.tsx';
import {
    getUserBasicsSave,
    subscribeUserBasicsSave,
    triggerUserBasicsSave,
} from '../../../helpers/userBasicsSave.tsx';
import { CLOSING_LINE_ANALYTICS_ENABLED } from '../../../helpers/closingLineAnalysis.ts';
import { useAgentPermissions } from '../../../helpers/useAgentPermissions.tsx';

const USER_TAB_LABELS: Record<string, string> = {
    basics: 'The Basics',
    transaction: 'Transactions',
    pending: 'Pending',
    freePlays: 'Free Plays',
    performance: 'Performance',
    closingLine: 'Closing Line',
};

const tabLabelFromPath = (pathname: string): string => {
    const match = pathname.match(/\/user\/[^/]+(?:\/([^/]+))?/i);
    const segment = match?.[1];
    if (!segment) return 'The Basics';
    return USER_TAB_LABELS[segment] ?? 'The Basics';
};

const OptionsUser = ({ visitedUserName }) => {

    const { user: currentUser, userIpData: userIpDataAdmin, setSelectedOption } = useUser();
    const { allowFreePlay } = useAgentPermissions(currentUser);
    const [isOpen, setIsOpen] = useState(false);
    const [, setButtonText] = useState('Save');
    const [, setModalId] = useState('');
    const [basicsSaveDisabled, setBasicsSaveDisabled] = useState(true);
    const location = useLocation();
    const selectedOption = tabLabelFromPath(location.pathname);
    const navState = (location.state ?? {}) as VisitedUserNavState & { from?: string };
    const from = navState.from;
    const tabNavState: VisitedUserNavState = {
        from,
        visitedUserPreview:
            navState.visitedUserPreview ??
            (visitedUserName ? peekVisitedUser(visitedUserName) : undefined),
    };
    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        const syncBasicsSave = () => {
            setBasicsSaveDisabled(getUserBasicsSave()?.disabled ?? true);
        };
        syncBasicsSave();
        return subscribeUserBasicsSave(syncBasicsSave);
    }, []);

    useEffect(() => {
        setSelectedOption(selectedOption);
    }, [selectedOption, setSelectedOption]);

    useEffect(() => {
        return () => {
            setSelectedOption('The Basics');
        };
    }, [setSelectedOption]);

    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname]);

    const isBasicsTab = selectedOption === 'The Basics';

    const handleOptionClick = (option: any) => {
        setSelectedOption(option);
        switch (option) {
            case 'The Basics':
                if (currentUser?.userName) {
                    logUserActivity(currentUser.userName, `View User Basic: ${visitedUserName}`, userIpDataAdmin);
                }
                setButtonText('Save');
                setModalId('');
                break;
            case 'Transactions':
                if (currentUser?.userName) {
                    logUserActivity(currentUser.userName, `View User Transactions ${visitedUserName}`, userIpDataAdmin);
                }
                setButtonText('New Transaction');
                setModalId('transactionModal');
                break;
            case 'Free Plays':
                if (currentUser?.userName) {
                    logUserActivity(currentUser.userName, `View User Free Plays  ${visitedUserName}`, userIpDataAdmin);
                }
                setButtonText('New Free Play');
                setModalId('freePlayModal');
                break;
            case 'Performance':
                if (currentUser?.userName) {
                    logUserActivity(currentUser.userName, `View User Performance  ${visitedUserName}`, userIpDataAdmin);
                }
                setButtonText('Performance');
                setModalId('performance');
                break;
            case 'Closing Line':
                if (currentUser?.userName) {
                    logUserActivity(currentUser.userName, `View Closing Line Analysis: ${visitedUserName}`, userIpDataAdmin);
                }
                setButtonText('Closing Line');
                setModalId('');
                break;
            default:
                setButtonText('Save');
        }
        toggleMenu();
    };

    return (
        <div className='container-user mt-2'>
            <div style={{ display: "flex", alignItems: "center" }}>
                <CgMenuGridR className={`icon-option ${isOpen ? 'selected' : ''}`} onClick={toggleMenu} />
                <span className='user-option'>{selectedOption}</span>
            </div>
            {isBasicsTab && (
                <button
                    type="button"
                    className='button-user button-color-user save-button-basics'
                    onClick={triggerUserBasicsSave}
                    disabled={basicsSaveDisabled}
                >
                    Save
                </button>
            )}
            {isOpen && (
                <div className={`dropdown-menu ${isOpen ? 'open' : ''}`}>
                    <div className='icon-menu-user'><IoCloseCircleOutline size={22} onClick={toggleMenu} /></div>
                    <div className="text-center mt-3">
                        <div className="row row-cols-3 row-cols-md-3 g-4">
                            <div className="col">
                                <Link to="basics" state={tabNavState} className='menu-options' onClick={() => handleOptionClick('The Basics')}>
                                    <div>
                                        <GrUserSettings className='icon-basics' />
                                        <div className="menu-letters">The Basics</div>
                                    </div>
                                </Link>
                            </div>
                            <div className="col">
                                <Link to="transaction" state={tabNavState} className='menu-options' onClick={() => handleOptionClick('Transactions')}>
                                    <div>
                                        <FaMoneyBillTransfer className='icon-transac' />
                                        <div className="menu-letters">Transactions</div>
                                    </div>
                                </Link>
                            </div>
                            <div className="col">
                                <Link to="pending" state={tabNavState} className='menu-options' onClick={() => handleOptionClick('Pending')}>
                                    <div>
                                        <MdPendingActions className='icon-pending' />
                                        <div className="menu-letters">Pending</div>
                                    </div>
                                </Link>
                            </div>
                            {(currentUser?.userType !== 'subAgent' || allowFreePlay) && (
                                <div className="col mt-md-3">
                                    <Link to="freePlays" state={tabNavState} className='menu-options' onClick={() => handleOptionClick('Free Plays')}>
                                        <div>
                                            <GiReceiveMoney className='icon-free' />
                                            <div className="menu-letters">Free Play</div>
                                        </div>
                                    </Link>
                                </div>
                            )}
                            <div className="col mt-md-3">
                                <Link to="performance" state={tabNavState} className='menu-options' onClick={() => handleOptionClick('Performance')}>
                                    <div>
                                        <GrDocumentPerformance className='icon-transac' />
                                        <div className="menu-letters">Performance</div>
                                    </div>
                                </Link>
                            </div>
                            {CLOSING_LINE_ANALYTICS_ENABLED && currentUser?.userType === 'masterAgent' && (
                                <div className="col mt-md-3">
                                    <Link to="closingLine" state={tabNavState} className='menu-options' onClick={() => handleOptionClick('Closing Line')}>
                                        <div>
                                            <MdOutlineAnalytics className='icon-transac' />
                                            <div className="menu-letters">Closing Line</div>
                                        </div>
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* <div>
                {renderButton()}
            </div> */}

            <div className="modal fade" id="freePlayModal" tabIndex={-1}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h1 className="modal-title fs-5" id="exampleModalLabel1">New Free Plays</h1>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <form>
                                <div className="mb-3">
                                    <span>Transaction:</span>
                                    <input type="text" className="form-control" id="transaction" name="transaction" autoComplete="transaction-currency"></input>
                                </div>
                                <div className="mb-3">
                                    <span>Amount:</span>
                                    <input type="text" className="form-control" id="amount" name="amount" autoComplete="transaction-amount"></input>
                                </div>
                                <div className="mb-3">
                                    <span>Description:</span>
                                    <textarea className="form-control" id="description1" name="description" autoComplete="off"></textarea>
                                </div>
                            </form>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-danger" data-bs-dismiss="modal">Cancel</button>
                            <button className="btn btn-success">Confirm</button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal fade" id="transactionModal" tabIndex={-1}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h1 className="modal-title fs-5" id="exampleModalLabel2">New Transaction</h1>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <form>
                                <div className="mb-3">
                                    <span>Transaction:</span>
                                    <input type="text" className="form-control" id="transaction2" name="transaction" autoComplete="transaction-currency"></input>
                                </div>
                                <div className="mb-3">
                                    <span>Description:</span>
                                    <textarea className="form-control" id="description2" name="description" autoComplete="off"></textarea>
                                </div>
                            </form>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-danger" data-bs-dismiss="modal">Cancel</button>
                            <button className="btn btn-success" data-bs-dismiss="modal">Confirm</button>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    )
}

export default OptionsUser;